import { useState } from "react";

interface SharePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

export default function SharePanel({ open, onClose, title }: SharePanelProps) {
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const url = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `${title} - 黑料`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, url });
        onClose();
      } catch {
        // 用户取消分享，忽略
      }
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openWindow = (target: string) => {
    window.open(target, "_blank", "noopener,noreferrer");
  };

  const options = [
    {
      key: "wechat",
      label: "微信",
      icon: "ri-wechat-fill",
      color: "bg-accent-500 text-background-50",
      action: copyLink,
    },
    {
      key: "weibo",
      label: "微博",
      icon: "ri-weibo-fill",
      color: "bg-primary-500 text-background-50",
      action: () =>
        openWindow(
          `https://service.weibo.com/share/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(shareText)}`,
        ),
    },
    {
      key: "qq",
      label: "QQ",
      icon: "ri-qq-fill",
      color: "bg-secondary-500 text-background-50",
      action: () =>
        openWindow(
          `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(url)}&title=${encodeURIComponent(shareText)}`,
        ),
    },
    {
      key: "link",
      label: "复制链接",
      icon: "ri-link-m",
      color: "bg-foreground-950 text-background-50",
      action: copyLink,
    },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <button
        type="button"
        aria-label="关闭分享"
        onClick={onClose}
        className="absolute inset-0 bg-foreground-950/50 cursor-pointer"
      />
      <div className="relative w-full sm:w-[420px] bg-background-50 rounded-t-lg sm:rounded-lg p-5 animate-fade-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-base">分享这条黑料</h3>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={option.action}
              className="flex flex-col items-center gap-2 cursor-pointer group"
            >
              <span
                className={`w-12 h-12 rounded-full flex items-center justify-center transition group-hover:scale-105 ${option.color}`}
              >
                <i className={`${option.icon} text-xl`} />
              </span>
              <span className="text-xs text-foreground-600">{option.label}</span>
            </button>
          ))}
        </div>
        {copied && (
          <p className="mt-4 text-xs text-center text-primary-600">
            链接已复制，去粘贴给朋友吧
          </p>
        )}
        {typeof navigator !== "undefined" && "share" in navigator && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="mt-4 w-full py-2.5 rounded-md border border-background-300 text-sm hover:border-primary-300 hover:text-primary-600 transition cursor-pointer"
          >
            使用系统分享
          </button>
        )}
      </div>
    </div>
  );
}